/* eslint-disable react-refresh/only-export-components */
/**
 * Connectivity Context
 *
 * Tracks BOTH browser online status AND API availability.
 * When API is down, queries should be paused to prevent spam.
 */

import react from 'react';
import type { JSX, ReactNode } from 'react';
import { onlineManager } from '@tanstack/react-query';

// --- Types ---

interface ConnectivityProviderProps {
  children: ReactNode;
}

interface ConnectivityState {
  /** Whether the browser reports we're online */
  isBrowserOnline: boolean;
  /** Whether the API is reachable (set by QueryCache callbacks) */
  isApiAvailable: boolean;
  /** Combined: true only if both browser online AND API available */
  isOnline: boolean;
  /** Mark API as unavailable (called by QueryCache onError) */
  setApiUnavailable: () => void;
  /** Mark API as available (called by QueryCache onSuccess) */
  setApiAvailable: () => void;
}

// --- Internal state ---

const ConnectivityContext = react.createContext<ConnectivityState | null>(null);

// --- useSyncExternalStore helpers (React 18 pattern for external subscriptions) ---

function getOnlineSnapshot(): boolean {
  return onlineManager.isOnline();
}

function subscribeToOnlineManager(callback: () => void): () => void {
  return onlineManager.subscribe(callback);
}

// --- Exports ---

export function ConnectivityProvider({ children }: ConnectivityProviderProps): JSX.Element {
  const isBrowserOnline = react.useSyncExternalStore(subscribeToOnlineManager, getOnlineSnapshot);
  const [isApiAvailable, setIsApiAvailable] = react.useState(true);

  const setApiUnavailable = react.useCallback(() => {
    setIsApiAvailable(false);
  }, []);

  const setApiAvailable = react.useCallback(() => {
    setIsApiAvailable(true);
  }, []);

  const value = react.useMemo<ConnectivityState>(
    () => ({
      isBrowserOnline,
      isApiAvailable,
      isOnline: isBrowserOnline && isApiAvailable,
      setApiUnavailable,
      setApiAvailable,
    }),
    [isBrowserOnline, isApiAvailable, setApiUnavailable, setApiAvailable],
  );

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity(): ConnectivityState {
  const context = react.useContext(ConnectivityContext);
  if (!context) {
    throw new Error('useConnectivity must be used within a ConnectivityProvider');
  }
  return context;
}

// Singleton for access from outside React (QueryCache callbacks)
let connectivityInstance: {
  isApiAvailable: () => boolean;
  setApiAvailable: () => void;
  setApiUnavailable: () => void;
} | null = null;

export function getConnectivityInstance(): typeof connectivityInstance {
  return connectivityInstance;
}

export function setConnectivityInstance(instance: typeof connectivityInstance): void {
  connectivityInstance = instance;
}
